"use client";
import React from "react";
import { Caveat } from "next/font/google";
import Image from "next/image";
import { motion } from "framer-motion";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export default function EyewearAppointmentsSection({ section }) {
  const title = section?.title || "Easy online appointments in different locations";
  const items = section?.items || [
    {
      bold: "Easy to use",
      text: "Your customers can book an appointment online, or using their mobile device."
    },
    {
      bold: "CRM pipeline",
      text: "Every slot booked creates an opportunity. It's up to you to turn it into a sale!"
    },
    {
      bold: "Multiple shops",
      text: "Odoo supports multi-company environments."
    }
  ];

  const scheduleImg = section?.scheduleImg || "/odoo/industries/eyewear/appointments-schedule.png";
  const calendarImg = section?.calendarImg || "/odoo/industries/eyewear/appointments-calendar.png";
  const crmImg = section?.crmImg || "/odoo/industries/eyewear/appointments-crm.png";

  return (
    <section className="py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* Title Section with Floating Calendar Icon */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 relative"
        >
          <div className="relative inline-block">
            {/* Top-Left Floating Teal Calendar Icon */}
            <div className="absolute -top-10 -left-12 sm:-left-16 w-12 sm:w-16 h-12 sm:h-16 text-teal-400 select-none pointer-events-none transform -rotate-12">
              <svg viewBox="0 0 64 64" fill="none" className="w-full h-full stroke-current" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                <rect x="8" y="12" width="48" height="44" rx="8" className="fill-teal-950/40" />
                <line x1="8" y1="24" x2="56" y2="24" strokeWidth="4" />
                <line x1="20" y1="6" x2="20" y2="14" strokeWidth="5" />
                <line x1="44" y1="6" x2="44" y2="14" strokeWidth="5" />
                <circle cx="20" cy="34" r="3" className="fill-teal-400" />
                <circle cx="32" cy="34" r="3" className="fill-teal-400" />
                <circle cx="44" cy="34" r="3" className="fill-teal-400" />
                <circle cx="20" cy="44" r="3" className="fill-teal-400" />
                <circle cx="32" cy="44" r="3" className="fill-teal-400" />
                <circle cx="44" cy="44" r="3" className="fill-teal-400" />
              </svg>
            </div>

            <h2 className={`text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight ${caveat.className} max-w-4xl mx-auto`}>
              {title}
            </h2>
          </div>
        </motion.div>

        {/* 3-Column Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 mb-16">
          {items.map((item, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.15 }}
              className="space-y-3"
            >
              {/* Circular Green Checkmark Icon */}
              <div className="w-10 h-10 rounded-full bg-teal-500/20 border border-teal-400/40 flex items-center justify-center text-teal-400 mb-4">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                {item.bold}
              </h3>

              <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                {item.text}
              </p>
            </motion.div>
          ))}
        </div>

        {/* 3-Step Process Flow Mockups */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="grid lg:grid-cols-12 gap-6 items-center"
        >
          {/* Step 1: Schedule Selection */}
          <div className="lg:col-span-4 relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-white transition-transform duration-300 hover:scale-[1.01]">
            <Image
              src={scheduleImg}
              alt="Schedule an Appointment interface"
              width={600}
              height={400}
              className="w-full h-auto object-cover rounded-2xl"
              priority
            />
          </div>

          {/* Connected Arrow 1 */}
          <div className="hidden lg:flex lg:col-span-1 justify-center text-purple-400 select-none">
            <svg className="w-10 h-10 transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </div>

          {/* Step 2: Calendar Date/Time Picker */}
          <div className="lg:col-span-4 relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-white transition-transform duration-300 hover:scale-[1.01]">
            <Image
              src={calendarImg}
              alt="Date and Time picker calendar interface"
              width={600}
              height={400}
              className="w-full h-auto object-cover rounded-2xl"
              priority
            />
          </div>

          {/* Connected Arrow 2 */}
          <div className="hidden lg:flex lg:col-span-1 justify-center text-purple-400 select-none">
            <svg className="w-10 h-10 transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </div>

          {/* Step 3: CRM Opportunities Pipeline */}
          <div className="lg:col-span-2 relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-white transition-transform duration-300 hover:scale-[1.01]">
            <Image
              src={crmImg}
              alt="CRM opportunity pipeline interface"
              width={400}
              height={500}
              className="w-full h-auto object-cover rounded-2xl"
              priority
            />
          </div>
        </motion.div>

      </div>
    </section>
  );
}
